import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import RichEditor from "@/components/RichEditor";
import { Badge } from "@/components/ui/badge";
import { ScrollText, Edit2, Archive, RotateCcw, ExternalLink, CalendarClock, FileCheck2, Eye, EyeOff } from "lucide-react";

export default function AdminPolicies() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all|published|hidden|archived
  const [form, setForm] = useState({
    title_ar: "", title_en: "", body_ar: "", body_en: "",
    version: "", published: true, effective_date: "", document_url: "",
  });

  const load = () => api.get("/admin/policies").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const activeCount = items.filter((p) => !p.is_archived && p.published).length;
  const visible = items.filter((p) => {
    if (statusFilter === "published") return !p.is_archived && p.published;
    if (statusFilter === "hidden") return !p.is_archived && !p.published;
    if (statusFilter === "archived") return p.is_archived;
    return true; // all
  });

  const openEdit = (p) => {
    setSelected(p.key);
    setForm({
      title_ar: p.title_ar, title_en: p.title_en, body_ar: p.body_ar, body_en: p.body_en,
      version: p.version || "1.0.0",
      published: p.published,
      effective_date: p.effective_date ? p.effective_date.slice(0, 10) : "",
      document_url: p.document_url || "",
    });
  };
  const save = async () => {
    try {
      const payload = { ...form };
      payload.effective_date = form.effective_date ? new Date(form.effective_date).toISOString() : null;
      await api.put(`/admin/policies/${selected}`, payload);
      toast.success("Policy updated"); setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (p) => { if (window.confirm(`Archive "${p.title_en}"?`)) { await api.post(`/admin/policies/${p.key}/archive`); load(); } };
  const restore = async (p) => { await api.post(`/admin/policies/${p.key}/restore`); load(); };
  const publishToggle = async (p) => {
    try { await api.put(`/admin/policies/${p.key}`, { published: !p.published }); load(); }
    catch (e) { console.error(e); }
  };

  return (
    <div data-testid="admin-policies">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Governance documents</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <ScrollText size={26} className="text-gold" /> Platform Policies
            <span className="chip !bg-white !text-navy !border-gold" data-testid="policy-count">{activeCount}</span>
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            Filter
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs px-2 py-1 border border-navy/20 rounded-sm bg-white"
                    data-testid="pol-status-filter">
              <option value="all">All ({items.length})</option>
              <option value="published">Published ({items.filter((p) => !p.is_archived && p.published).length})</option>
              <option value="hidden">Hidden ({items.filter((p) => !p.is_archived && !p.published).length})</option>
              <option value="archived">Archived ({items.filter((p) => p.is_archived).length})</option>
            </select>
          </label>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.map((p) => (
          <div key={p.key} className="p-5 flex items-center justify-between gap-3" data-testid={`pol-row-${p.key}`}>
            <div className="min-w-0">
              <div className="text-navy font-medium flex items-center gap-2">
                <FileCheck2 size={14} className="text-gold" /> {p.title_en}
                <span className="chip !bg-white !text-navy !border-navy/20 !py-0 !px-1.5 text-[10px]">v{p.version}</span>
                {p.is_archived
                  ? <Badge className="bg-navy/20 text-navy/70">Archived</Badge>
                  : p.published
                    ? <Badge className="bg-emerald-100 text-emerald-800">Published</Badge>
                    : <Badge className="bg-amber-100 text-amber-800">Hidden</Badge>}
                {p.audience === "internal" && <Badge className="bg-purple-100 text-purple-800">Internal</Badge>}
              </div>
              <div className="text-xs text-navy/60 mt-1 flex flex-wrap gap-3">
                <span>{p.title_ar}</span>
                {p.effective_date && (
                  <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> Effective {new Date(p.effective_date).toLocaleDateString()}</span>
                )}
                {p.document_url && (
                  <a href={p.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gold"><ExternalLink size={11} /> Document</a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <button onClick={() => openEdit(p)} className="text-navy underline decoration-gold underline-offset-4 text-sm" data-testid={`pol-edit-${p.key}`}>
                <Edit2 size={12} className="inline" /> Edit
              </button>
              <a href={`/api/policies/${p.key}/pdf`} target="_blank" rel="noopener noreferrer" className="text-navy underline decoration-gold underline-offset-4 text-sm" data-testid={`pol-pdf-${p.key}`}>
                <ExternalLink size={12} className="inline" /> PDF
              </a>
              {p.is_archived
                ? <button onClick={() => restore(p)} className="text-emerald-700 text-sm" data-testid={`pol-restore-${p.key}`}><RotateCcw size={12} className="inline" /> Restore</button>
                : (<>
                  <button onClick={() => publishToggle(p)} className={p.published ? "text-amber-700 text-sm" : "text-emerald-700 text-sm"} data-testid={`pol-publish-${p.key}`}>
                    {p.published ? <><EyeOff size={12} className="inline" /> Hide</> : <><Eye size={12} className="inline" /> Publish</>}
                  </button>
                  <button onClick={() => archive(p)} className="text-amber-700 text-sm" data-testid={`pol-archive-${p.key}`}><Archive size={12} className="inline" /> Archive</button>
                </>)}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-3xl mx-auto my-6 p-8 rounded-md border border-gold/30" onClick={(e) => e.stopPropagation()} data-testid="pol-edit-modal">
            <h2 className="text-2xl text-navy">Edit: {selected}</h2>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input placeholder="Title (AR)" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} />
              <Input placeholder="Title (EN)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
              <Input placeholder="Version (e.g. 1.2.0)" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="pol-version" />
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="pol-effective" />
              <Input className="md:col-span-2" placeholder="Document URL (PDF / DOCX)" value={form.document_url} onChange={(e) => setForm({ ...form, document_url: e.target.value })} data-testid="pol-doc-url" />
              <RichEditor testId="pol-body-ar" dir="rtl" value={form.body_ar} onChange={(html) => setForm({ ...form, body_ar: html })} />
              <RichEditor testId="pol-body-en" dir="ltr" value={form.body_en} onChange={(html) => setForm({ ...form, body_en: html })} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="text-navy/60" onClick={() => setSelected(null)}>Cancel</button>
              <button className="btn-gold" onClick={save} data-testid="pol-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
